import React, { useEffect, useState } from "react";
import { Checkbox, Modal, notification } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";

import { useDeleteRecurringPaymentMutation } from "../../api/graphql/mutations/recurringPayment";
import { showGraphQlErrorNotification } from "../../utils";
import { ITransaction, TransactionSource } from "../../types";
import RecurringRuleModal, {
  RecurringRuleTransaction,
} from "./RecurringRuleModal";

export interface RecurringPaymentToggleProps {
  transaction: RecurringRuleTransaction;
  recurringPaymentRuleId?: number | null;
}

export const RECURRING_PAYMENT_TOGGLE_TEXT = "Wiederkehrende Zahlung";

export const shouldDisplayToggle = (transaction: ITransaction): boolean => {
  return (
    transaction.source === TransactionSource.SOLARIS && !!transaction.verified
  );
};

const RecurringPaymentToggle = ({
  transaction,
  recurringPaymentRuleId,
}: RecurringPaymentToggleProps) => {
  const [ruleId, setRecurringPaymentRuleId] = useState<
    number | undefined | null
  >(recurringPaymentRuleId);
  const [showModal, setShowModal] = useState(false);

  // Keep the checkbox in sync when the parent passes a new value (e.g. after an
  // external refetch of the transaction list, not driven by this component).
  useEffect(() => {
    setRecurringPaymentRuleId(recurringPaymentRuleId);
  }, [recurringPaymentRuleId]);

  const [deleteRecurringPaymentRule, { loading: deleting }] =
    useDeleteRecurringPaymentMutation();

  const handleDelete = async () => {
    try {
      await deleteRecurringPaymentRule({
        variables: { transactionId: transaction.id },
      });
      setRecurringPaymentRuleId(null);
      notification.success({ message: "Recurring payment was deleted" });
    } catch (err) {
      showGraphQlErrorNotification(
        "An error occurred while deleting recurring payment:",
        err
      );
    }
  };

  const handleChange = (event: CheckboxChangeEvent) => {
    if (deleting) {
      return;
    }

    if (ruleId) {
      // Unchecking deletes the rule — confirm before the destructive action.
      Modal.confirm({
        title: "Delete recurring payment rule?",
        content:
          "This deletes the autocategorization rule created for this recurring payment.",
        okText: "Delete rule",
        okType: "danger",
        cancelText: "Cancel",
        onOk: handleDelete,
      });
      return;
    }

    // Checking opens the confirm-before-create modal; the rule is written only
    // after the operator confirms with Create.
    setShowModal(true);
  };

  return (
    <>
      <Checkbox checked={!!ruleId} onChange={handleChange} disabled={deleting}>
        {RECURRING_PAYMENT_TOGGLE_TEXT}
      </Checkbox>
      {showModal && (
        <RecurringRuleModal
          transaction={transaction}
          onCreated={(newRuleId) => {
            setRecurringPaymentRuleId(newRuleId);
            setShowModal(false);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default RecurringPaymentToggle;
